import { AlertCircle, Loader2, RefreshCw } from "lucide-react"
import { Badge } from "@/modules/shared/ui/components/badge"
import { Button } from "@/modules/shared/ui/components/button"
import {
  getScoringFailureLabel,
  ScoringFailureReason,
  ScoringStatus,
} from "../../enums/ScoringStatus"
import { TooltipWrap } from "@/modules/shared/ui/components/tooltip-wrap"

interface Props {
  score: number
  scoringStatus?: ScoringStatus
  scoringFailureReason?: ScoringFailureReason
  onRescore?: () => void
  isRescoring?: boolean
  showRescoreButton?: boolean
  className?: string
}

// Renders "Match Score: N%" for scored applications and an "Unscored (reason)" pill
// otherwise. Optionally shows a Rescore button next to the badge.
export function ScoringStatusBadge({
  score,
  scoringStatus,
  scoringFailureReason,
  onRescore,
  isRescoring,
  showRescoreButton,
  className,
}: Props) {
  const status = scoringStatus ?? "Scored"

  if (isRescoring) {
    return (
      <Badge
        variant="outline"
        className={`text-sm px-4 py-1.5 rounded-full font-medium border-blue-400/60 text-blue-800 bg-blue-50 ${className ?? ""}`}
      >
        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        Rescoring…
      </Badge>
    )
  }

  if (status === "Scored") {
    return (
      <TooltipWrap
       title="Match Score"
       description="An AI-generated estimate of how well this candidate's CV matches the vacancy's requirements and weighted skills. Use it to prioritise review — not as a sole hire/reject decision."
       > 
      <Badge
        className={`bg-primary/10 text-primary hover:bg-primary/20 text-sm px-4 py-1.5 rounded-full font-medium shadow-sm border-0 ${className ?? ""}`}
      >
        Match Score: {score}%
      </Badge>
      </TooltipWrap>
    )
  }

  const label =
    status === "Pending"
      ? "Scoring in progress"
      : `Unscored ${getScoringFailureLabel(scoringFailureReason ?? "Unknown") || "Unknown error"}`
  
  const failureDescriptions: Record<ScoringFailureReason, string> = {
    None: "",
    InsufficientWalletBalance:
      "The organization's wallet didn't have enough credit to run AI scoring. Top up the wallet, then use Rescore.",
    AiServiceError:
      "The AI scoring service failed unexpectedly. Try Rescore again — if it keeps failing, contact support.",
    CvParseError:
      "The candidate's CV couldn't be read for scoring, often due to a scanned or corrupted file. Check the uploaded CV.",
    Unknown: "Scoring failed for an unspecified reason. Try Rescore again.",
  }

  return (
    <div className={`inline-flex items-center gap-2 ${className ?? ""}`}>
      <TooltipWrap
        title={status === "Pending" ? "Scoring in Progress" : "Scoring Failed"}
        description={
          status === "Pending"
          ? "AI scoring hasn't finished yet. The match score will appear once it completes."
          : failureDescriptions[scoringFailureReason ?? "Unknown"]
        }
      >
      <Badge
        variant="outline"
        className="text-sm px-4 py-1.5 rounded-full font-medium border-yellow-400/60 text-yellow-800 bg-yellow-50"
      >
        <AlertCircle className="w-3.5 h-3.5 mr-1.5" />
        {label}
      </Badge>
      </TooltipWrap>
      {showRescoreButton && status !== "Pending" && onRescore && (
        <TooltipWrap
          title="Rescore"
          description="Re-runs AI scoring for this candidate using the organization's wallet credit."
        >
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onRescore}
          disabled={isRescoring}
          className="h-8"
        >
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
          Rescore
        </Button>
        </TooltipWrap>
      )}
    </div>
  )
}
